import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCIES } from '../lib/utils';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/apiError';
import { analytics } from '../lib/analytics';
import { Check, User, CreditCard, Loader2, Palette, Upload, X, Building, Phone, Globe, Coins, FileText, Gift, ChevronDown, ChevronUp, ExternalLink, Unlink, AlertCircle, CheckCircle2, Trash2, AlertTriangle, Eye, Type, Image as ImageIcon, Copy, Users, Lock } from 'lucide-react';

// Annual pricing/savings per paid tier — used for the plan cards toggle and
// the "switch to annual instead" cancel-flow retention offer.
const ANNUAL_PLAN_INFO = {
  standard: { annualPrice: 55, savingsPercent: 8 },
  pro: { annualPrice: 80, savingsPercent: 17 },
};

const Settings = () => {
  const { user, logout, logoutAll, refreshUser, api } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processingPayment, setProcessingPayment] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');
  const [switchingInterval, setSwitchingInterval] = useState(false);
  
  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Sign out everywhere state
  const [showLogoutAllDialog, setShowLogoutAllDialog] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  // Change-email flow state
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  
  // Stripe Connect state
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [loadingStripeStatus, setLoadingStripeStatus] = useState(true);
  const [connectingStripe, setConnectingStripe] = useState(false);
  
  // Branding state
  const [branding, setBranding] = useState({
    logo_base64: null,
    primary_color: '#233FE0',
    secondary_color: '#211C16',
    accent_color: '#233FE0',
    business_name: '',
    business_address: '',
    business_phone: '',
    business_website: '',
    default_currency: 'USD',
    invoice_prefix: 'INV',
    font_pairing: 'classic',
    logo_size: 'medium',
    default_template: 'brutalist',
    invoice_footer: '',
    palette_preset: 'terracotta'
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Invoice Designer state
  const [designerOptions, setDesignerOptions] = useState(null);
  const [previewDebounceTimer, setPreviewDebounceTimer] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [promoValidated, setPromoValidated] = useState(null);
  
  // Expanded features state
  const [expandedPlans, setExpandedPlans] = useState({});
  
  // Subscription cancel state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  
  // Referral state
  const [referralData, setReferralData] = useState(null);
  const [loadingReferral, setLoadingReferral] = useState(true);

  // colorPresets removed - now uses designerOptions.palette_presets from backend (single source of truth)

  const isPaidUser = user?.subscription_plan && user.subscription_plan !== 'free';
  const isProUser = user?.subscription_plan === 'pro';

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: '/month',
      features: [
        '2 invoices per month',
        'Brutalist & Classic templates',
        'Client management',
        'Basic dashboard stats',
      ],
      extraFeatures: [
        'Email invoices to clients',
        'PDF downloads',
        'Online payments (Stripe)',
        '"Powered by CreateCollect" branding',
      ],
      current: user?.subscription_plan === 'free'
    },
    {
      id: 'standard',
      name: 'Standard',
      price: billingInterval === 'year' ? '$55' : '$5',
      period: billingInterval === 'year' ? '/year' : '/month',
      savings: billingInterval === 'year' ? 8 : null,
      features: [
        '5 invoices per month',
        'Email marketing — 500 emails/month',
        'All 5 invoice templates',
        'Business reports & analytics',
      ],
      extraFeatures: [
        'Remove "Powered by" branding',
        'Custom invoice prefix',
        'Multi-currency support',
        'Tax calculations',
        'Partial payments tracking',
        'CSV export',
        'Logo & color branding',
        'Instant AI support chat',
      ],
      current: user?.subscription_plan === 'standard',
      popular: true
    },
    {
      id: 'pro',
      name: 'Pro',
      price: billingInterval === 'year' ? '$80' : '$8',
      period: billingInterval === 'year' ? '/year' : '/month',
      savings: billingInterval === 'year' ? 17 : null,
      features: [
        'Unlimited invoices',
        'Email marketing — 2,000 emails/month',
        'Everything in Standard',
        'Scheduled invoice sending',
      ],
      extraFeatures: [
        'Recurring invoices',
        'Automated payment reminders',
        'Duplicate invoices',
        'Advanced analytics',
        'Priority support',
        'Instant AI support chat',
        'Early access to new features',
      ],
      current: user?.subscription_plan === 'pro'
    }
  ];

  const toggleExpandPlan = (planId) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  // Load branding from user
  useEffect(() => {
    if (user?.branding) {
      setBranding(prev => ({
        ...prev,
        ...user.branding
      }));
    }
  }, [user]);

  // Load designer options
  useEffect(() => {
    const fetchDesignerOptions = async () => {
      try {
        const response = await api.get('/branding/designer-options');
        setDesignerOptions(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to load designer options:', error);
      }
    };
    fetchDesignerOptions();
  }, [api]);

  // Load Stripe Connect status
  useEffect(() => {
    fetchStripeConnectStatus();
    
    // Check for Stripe Connect return
    if (searchParams.get('stripe_connected') === 'true') {
      toast.success('Stripe account connected successfully!');
      fetchStripeConnectStatus();
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (searchParams.get('stripe_refresh') === 'true') {
      toast.info('Please complete your Stripe account setup');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);

  // Load referral data
  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        const response = await api.get('/referral');
        setReferralData(response.data);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to load referral data:', error);
      } finally {
        setLoadingReferral(false);
      }
    };
    fetchReferralData();
  }, [api]);

  const copyReferralLink = () => {
    if (referralData?.share_url) {
      navigator.clipboard.writeText(referralData.share_url);
      toast.success('Referral link copied!');
    }
  };

  const fetchStripeConnectStatus = async () => {
    setLoadingStripeStatus(true);
    try {
      const response = await api.get('/connect/status');
      setStripeConnectStatus(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to fetch Stripe status:', error);
    } finally {
      setLoadingStripeStatus(false);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const response = await api.post('/connect/create-account', {
        origin_url: window.location.origin
      });
      
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create Stripe account'));
      setConnectingStripe(false);
    }
  };

  const handleCompleteStripeSetup = async () => {
    setConnectingStripe(true);
    try {
      const response = await api.post('/connect/onboarding-link', {
        origin_url: window.location.origin
      });
      
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to get onboarding link'));
      setConnectingStripe(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    try {
      const response = await api.post('/connect/dashboard-link');
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Stripe account? You will no longer be able to receive payments.')) {
      return;
    }
    
    try {
      await api.post('/connect/disconnect');
      toast.success('Stripe account disconnected');
      setStripeConnectStatus({ connected: false, status: 'not_connected' });
    } catch (error) {
      toast.error('Failed to disconnect Stripe account');
    }
  };

  // Check for payment completion
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // One-time: pre-select the billing interval the visitor chose on the homepage
  // pricing toggle before signing up. Read-once and cleared — never auto-triggers checkout.
  useEffect(() => {
    const pending = localStorage.getItem('cc_pending_billing_interval');
    if (pending === 'year' || pending === 'month') {
      setBillingInterval(pending);
      localStorage.removeItem('cc_pending_billing_interval');
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      
      if (response.data.status === 'paid') {
        toast.success('Subscription upgraded successfully!');
        await refreshUser();
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error checking payment:', error);
    }
  };

  const handleUpgrade = async (planId) => {
    if (planId === 'free' || planId === user?.subscription_plan) return;

    setProcessingPayment(true);
    analytics.beginCheckout(planId);
    try {
      const response = await api.post('/subscriptions/checkout', {
        plan: planId,
        billing_interval: billingInterval,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to start checkout'));
      setProcessingPayment(false);
    }
  };

  const handleSwitchInterval = async (targetInterval) => {
    const interval = targetInterval || billingInterval;
    setSwitchingInterval(true);
    try {
      const response = await api.post('/subscriptions/switch-interval', {
        billing_interval: interval
      });
      const charged = response.data.charged_amount;
      toast.success(
        interval === 'year'
          ? `Switched to annual billing${charged ? ` — $${charged.toFixed(2)} charged today (prorated)` : ''}.`
          : 'Switched to monthly billing.'
      );
      setShowCancelDialog(false);
      await refreshUser();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to switch billing interval'));
    } finally {
      setSwitchingInterval(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const response = await api.post('/subscriptions/cancel');
      toast.success(response.data.message);
      setShowCancelDialog(false);
      await refreshUser();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to cancel subscription'));
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  const handleRequestEmailChange = async (e) => {
    if (e) e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) {
      toast.error('Enter your new email address.');
      return;
    }
    if (trimmed === (user?.email || '').toLowerCase()) {
      toast.error('That is already the email on file.');
      return;
    }
    setSavingEmail(true);
    try {
      const { data } = await api.post('/auth/request-email-change', { new_email: trimmed });
      toast.success(data?.message || `Check ${trimmed} to confirm the change.`);
      setPendingEmail(data?.pending_email || trimmed);
      setNewEmail('');
      setEditingEmail(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not request the email change.'));
    } finally {
      setSavingEmail(false);
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    try {
      const response = await api.post('/promo/validate', { code: promoCode });
      setPromoValidated(response.data);
      toast.success(`Valid code! ${response.data.remaining_uses} uses remaining.`);
    } catch (error) {
      setPromoValidated(null);
      toast.error(getApiErrorMessage(error, 'Invalid promo code'));
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setApplyingPromo(true);
    try {
      const response = await api.post('/promo/apply', { code: promoCode });
      toast.success(response.data.message);
      setPromoCode('');
      setPromoValidated(null);
      await refreshUser();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to apply promo code'));
    } finally {
      setApplyingPromo(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/branding/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setBranding(prev => ({ ...prev, logo_base64: response.data.logo_base64 }));
      toast.success('Logo uploaded successfully');
      await refreshUser();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to upload logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = async () => {
    setSavingBranding(true);
    try {
      await api.put('/branding', { logo_base64: '' });
      setBranding(prev => ({ ...prev, logo_base64: null }));
      toast.success('Logo removed');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to remove logo');
    } finally {
      setSavingBranding(false);
    }
  };

  const applyPreset = (preset) => {
    setBranding(prev => ({
      ...prev,
      primary_color: preset.primary,
      secondary_color: preset.secondary,
      accent_color: preset.accent
    }));
  };

  const saveBranding = async () => {
    setSavingBranding(true);
    try {
      await api.put('/branding', branding);
      toast.success('Branding saved successfully');
      await refreshUser();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save branding'));
    } finally {
      setSavingBranding(false);
    }
  };

  // Invoice Designer: Load preview with debounce
  const loadDesignerPreview = (brandingToUse = null) => {
    // Clear existing timer
    if (previewDebounceTimer) {
      clearTimeout(previewDebounceTimer);
    }
    
    // Set new timer for 600ms debounce
    const timer = setTimeout(async () => {
      setLoadingPreview(true);
      try {
        // First save branding temporarily (unsaved overrides)
        const previewBranding = brandingToUse || branding;
        await api.put('/branding', previewBranding);
        
        // Then fetch preview
        const response = await api.get(`/templates/${previewBranding.default_template || 'brutalist'}/preview`, {
          params: { format: 'png' },
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }));
        
        // Revoke old URL
        if (previewPdfUrl) {
          window.URL.revokeObjectURL(previewPdfUrl);
        }
        setPreviewPdfUrl(url);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to load preview:', error);
      } finally {
        setLoadingPreview(false);
      }
    }, 600);
    
    setPreviewDebounceTimer(timer);
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewPdfUrl) {
        window.URL.revokeObjectURL(previewPdfUrl);
      }
      if (previewDebounceTimer) {
        clearTimeout(previewDebounceTimer);
      }
    };
  }, [previewPdfUrl, previewDebounceTimer]);

  // Handle designer field change with live preview
  const handleDesignerChange = (field, value) => {
    const newBranding = { ...branding, [field]: value };
    
    // Apply palette colors when preset changes
    if (field === 'palette_preset' && designerOptions?.palette_presets?.[value]) {
      const palette = designerOptions.palette_presets[value];
      newBranding.primary_color = palette.primary_color;
      newBranding.secondary_color = palette.secondary_color;
      newBranding.accent_color = palette.accent_color;
    }
    
    setBranding(newBranding);
    loadDesignerPreview(newBranding);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await api.delete('/auth/account');
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete account'));
    } finally {
      setDeletingAccount(false);
    }
  };

  const handleLogoutAll = async () => {
    setSigningOutAll(true);
    try {
      await logoutAll();
      toast.success('Signed out of all devices');
      navigate('/login');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to sign out of all devices'));
    } finally {
      setSigningOutAll(false);
      setShowLogoutAllDialog(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-4xl mx-auto space-y-8" data-testid="settings-page">
        <EmailVerificationBanner message="Verify your email to unlock sending invoices, campaigns and connecting Stripe." />
        {/* Header */}
        <div>
          <h1 className="font-heading font-semibold text-3xl tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account, branding, and preferences</p>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  user?.name?.charAt(0)?.toUpperCase() || 'U'
                )}
              </div>
              <div>
                <p className="font-medium text-lg">{user?.name}</p>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            {/* Email change */}
            <div className="pt-4 border-t space-y-3" data-testid="settings-email-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium">Account email</p>
                  <p className="text-xs text-muted-foreground">
                    Used for login and account notifications.
                  </p>
                </div>
                {!editingEmail && !pendingEmail && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditingEmail(true); setNewEmail(''); }}
                    data-testid="settings-open-change-email-btn"
                  >
                    Change email
                  </Button>
                )}
              </div>

              {pendingEmail && !editingEmail && (
                <div
                  className="rounded-lg border border-foreground/15 bg-muted/40 p-3 text-sm"
                  data-testid="settings-pending-email-notice"
                >
                  <p>
                    Check <strong>{pendingEmail}</strong> to confirm the change. Your account email stays
                    <strong> {user?.email}</strong> until you click the link.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setEditingEmail(true); setNewEmail(''); }}
                    className="mt-2 text-xs text-primary hover:underline"
                    data-testid="settings-change-email-again-btn"
                  >
                    Change to a different address
                  </button>
                </div>
              )}

              {editingEmail && (
                <form
                  onSubmit={handleRequestEmailChange}
                  className="space-y-3"
                  data-testid="settings-change-email-form"
                >
                  <Label htmlFor="settings-new-email" className="text-xs uppercase tracking-widest font-mono opacity-70">
                    New email address
                  </Label>
                  <Input
                    id="settings-new-email"
                    type="email"
                    placeholder="you@example.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    autoFocus
                    required
                    disabled={savingEmail}
                    data-testid="settings-new-email-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    We&rsquo;ll send a confirmation link to the new address. Your email doesn&rsquo;t change until you click it.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="submit"
                      disabled={savingEmail || !newEmail.trim()}
                      className="gap-2"
                      data-testid="settings-send-change-email-btn"
                    >
                      {savingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Send confirmation link
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => { setEditingEmail(false); setNewEmail(''); }}
                      data-testid="settings-cancel-change-email-btn"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button variant="destructive" onClick={handleLogout} data-testid="logout-btn">
                Log out
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stripe Connect - Payment Setup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Setup
            </CardTitle>
            <CardDescription>Connect your Stripe account to receive invoice payments directly</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStripeStatus ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading payment status...
              </div>
            ) : stripeConnectStatus?.connected ? (
              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex items-center gap-3">
                  {stripeConnectStatus.status === 'active' ? (
                    <Badge className="bg-emerald-500 hover:bg-emerald-600 gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Active
                    </Badge>
                  ) : stripeConnectStatus.status === 'pending' ? (
                    <Badge variant="secondary" className="gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Pending Review
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1 text-amber-600 border-amber-600">
                      <AlertCircle className="w-3 h-3" />
                      Setup Incomplete
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">{stripeConnectStatus.message}</span>
                </div>

                {/* Account Details */}
                <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Charges</span>
                    <span className={stripeConnectStatus.charges_enabled ? 'text-emerald-600' : 'text-amber-600'}>
                      {stripeConnectStatus.charges_enabled ? '✓ Enabled' : '○ Not enabled'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Payouts</span>
                    <span className={stripeConnectStatus.payouts_enabled ? 'text-emerald-600' : 'text-amber-600'}>
                      {stripeConnectStatus.payouts_enabled ? '✓ Enabled' : '○ Not enabled'}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {stripeConnectStatus.status !== 'active' && (
                    <Button onClick={handleCompleteStripeSetup} disabled={connectingStripe}>
                      {connectingStripe ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Complete Setup
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleOpenStripeDashboard} className="gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Stripe Dashboard
                  </Button>
                  <Button variant="ghost" onClick={handleDisconnectStripe} className="gap-2 text-destructive hover:text-destructive">
                    <Unlink className="w-4 h-4" />
                    Disconnect
                  </Button>
                </div>
                
                {/* Fee disclosure */}
                <div className="text-sm text-muted-foreground border-t pt-4 mt-2">
                  <strong>Card processing fee:</strong>{' '}
                  {stripeConnectStatus.platform_fee_percent || 3.4}% + ${((stripeConnectStatus.platform_fee_fixed_cents || 30) / 100).toFixed(2)} per transaction, deducted automatically from each payout.
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-dashed">
                  <p className="text-sm text-muted-foreground mb-3">
                    Connect your Stripe account to receive payments directly from your clients when they pay invoices online.
                  </p>
                  <ul className="text-sm space-y-1 text-muted-foreground mb-4">
                    <li>✓ Payments go directly to your bank account</li>
                    <li>✓ Secure, PCI-compliant processing</li>
                    <li>✓ Track all payments in your Stripe dashboard</li>
                  </ul>
                  <Button
                    onClick={handleConnectStripe}
                    disabled={connectingStripe || !user?.email_verified}
                    title={!user?.email_verified ? 'Verify your email to connect Stripe.' : undefined}
                    className="gap-2"
                  >
                    {connectingStripe ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4" />
                    )}
                    Connect Stripe Account
                  </Button>
                </div>
                {/* Fee disclosure - shown BEFORE onboarding */}
                <p className="text-xs text-muted-foreground">
                  You&apos;ll be redirected to Stripe to complete the setup process.
                </p>
                <div className="text-sm text-muted-foreground border-t pt-3">
                  <strong>Card processing fee:</strong>{' '}
                  {stripeConnectStatus?.platform_fee_percent || 3.4}% + ${((stripeConnectStatus?.platform_fee_fixed_cents || 30) / 100).toFixed(2)} per transaction, deducted automatically from each payout.
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Branding */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Invoice Branding
            </CardTitle>
            <CardDescription>Customize how your invoices look to clients</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo Upload */}
            <div className="space-y-3">
              <Label>Logo</Label>
              <div className="flex items-start gap-4">
                <div 
                  className="w-32 h-20 border-2 border-dashed rounded-xl flex items-center justify-center bg-muted/50 overflow-hidden cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {branding.logo_base64 ? (
                    <img 
                      src={branding.logo_base64} 
                      alt="Logo" 
                      className="max-w-full max-h-full object-contain p-1" 
                    />
                  ) : (
                    <Upload className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-upload-input"
                />
                <div className="space-y-2 flex-1">
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      data-testid="upload-logo-btn"
                    >
                      {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload Logo'}
                    </Button>
                    {branding.logo_base64 && (
                      <Button variant="ghost" size="sm" onClick={removeLogo} className="text-destructive">
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p className="font-medium">Best practices:</p>
                    <ul className="list-disc list-inside space-y-0.5 text-muted-foreground/80">
                      <li>Recommended size: <span className="font-mono">300×100px</span> or similar aspect ratio</li>
                      <li>Formats: PNG (best for transparency), JPG, or WebP</li>
                      <li>Max file size: 5MB</li>
                      <li>Use a horizontal/landscape logo for best results</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Color Scheme */}
            <div className="space-y-3">
              <Label>Color Scheme</Label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {designerOptions?.palette_presets && Object.entries(designerOptions.palette_presets).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyPreset({ primary: preset.primary_color, secondary: preset.secondary_color, accent: preset.accent_color })}
                    className="p-2 rounded-lg border hover:border-primary transition-colors text-center"
                    data-testid={`preset-${key}`}
                  >
                    <div className="flex justify-center gap-1 mb-1">
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary_color }} />
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary_color }} />
                      <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent_color }} />
                    </div>
                    <span className="text-xs">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Colors */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primary-color">Primary Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="primary-color"
                    value={branding.primary_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="w-12 h-10 rounded cursor-pointer border-0"
                    data-testid="primary-color-input"
                  />
                  <Input
                    value={branding.primary_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondary-color">Secondary Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="secondary-color"
                    value={branding.secondary_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="w-12 h-10 rounded cursor-pointer border-0"
                    data-testid="secondary-color-input"
                  />
                  <Input
                    value={branding.secondary_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="accent-color">Accent Color</Label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    id="accent-color"
                    value={branding.accent_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="w-12 h-10 rounded cursor-pointer border-0"
                    data-testid="accent-color-input"
                  />
                  <Input
                    value={branding.accent_color}
                    onChange={(e) => setBranding(prev => ({ ...prev, accent_color: e.target.value }))}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Default Currency */}
            <div className="space-y-2">
              <Label htmlFor="default-currency" className="flex items-center gap-2">
                <Coins className="w-4 h-4" />
                Default Currency
              </Label>
              <Select 
                value={branding.default_currency} 
                onValueChange={(v) => setBranding(prev => ({ ...prev, default_currency: v }))}
              >
                <SelectTrigger className="w-64" data-testid="default-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CURRENCIES).map(([code, info]) => (
                    <SelectItem key={code} value={code}>
                      {info.symbol} {code} - {info.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                This currency will be pre-selected when creating new invoices
              </p>
            </div>

            {/* Custom Invoice Prefix (Paid tiers) */}
            <div className="space-y-2">
              <Label htmlFor="invoice-prefix" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Invoice Prefix
                {!isPaidUser && (
                  <Badge variant="outline" className="text-xs ml-2">Paid Plan</Badge>
                )}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="invoice-prefix"
                  value={branding.invoice_prefix || 'INV'}
                  onChange={(e) => setBranding(prev => ({ ...prev, invoice_prefix: e.target.value.toUpperCase() }))}
                  placeholder="INV"
                  className="w-32 font-mono"
                  maxLength={10}
                  disabled={!isPaidUser}
                  data-testid="invoice-prefix-input"
                />
                <span className="text-muted-foreground font-mono">-0001</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {isPaidUser 
                  ? "Customize how your invoice numbers appear (e.g., ACME-0001, 2025-0001)"
                  : "Upgrade to customize your invoice numbering prefix"}
              </p>
            </div>

            {/* Invoice Preview */}
            <div className="space-y-3">
              <Label>Preview</Label>
              <div className="border rounded-xl p-6 bg-white">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    {branding.logo_base64 ? (
                      <img 
                        src={branding.logo_base64} 
                        alt="Logo" 
                        className="max-w-[120px] max-h-[50px] w-auto h-auto object-contain" 
                      />
                    ) : (
                      <div className="w-16 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-xs">
                        Logo
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{branding.business_name || user?.name || 'Your Business'}</p>
                      <p className="text-sm text-gray-500">{branding.business_website || 'yourwebsite.com'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold" style={{ color: branding.primary_color }}>INVOICE</p>
                    <p className="text-sm text-gray-500 font-mono">{branding.invoice_prefix || 'INV'}-0001</p>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-500">Design Services</span>
                    <span className="text-gray-900">$1,500.00</span>
                  </div>
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-gray-500">Photography</span>
                    <span className="text-gray-900">$500.00</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span style={{ color: branding.primary_color }}>$2,000.00</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Info */}
            <div className="space-y-4 pt-4 border-t">
              <Label className="text-base">Business Information (appears on invoices)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="business-name" className="flex items-center gap-2">
                    <Building className="w-4 h-4" /> Business Name
                  </Label>
                  <Input
                    id="business-name"
                    value={branding.business_name || ''}
                    onChange={(e) => setBranding(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder={user?.name || 'Your Business Name'}
                    data-testid="business-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business-phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Phone
                  </Label>
                  <Input
                    id="business-phone"
                    value={branding.business_phone || ''}
                    onChange={(e) => setBranding(prev => ({ ...prev, business_phone: e.target.value }))}
                    placeholder="+1 234 567 8900"
                    data-testid="business-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business-website" className="flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Website
                  </Label>
                  <Input
                    id="business-website"
                    value={branding.business_website || ''}
                    onChange={(e) => setBranding(prev => ({ ...prev, business_website: e.target.value }))}
                    placeholder="www.yoursite.com"
                    data-testid="business-website-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business-address">Address</Label>
                  <Input
                    id="business-address"
                    value={branding.business_address || ''}
                    onChange={(e) => setBranding(prev => ({ ...prev, business_address: e.target.value }))}
                    placeholder="123 Creative St, City"
                    data-testid="business-address-input"
                  />
                </div>
              </div>
            </div>

            <Button onClick={saveBranding} disabled={savingBranding} className="w-full sm:w-auto" data-testid="save-branding-btn">
              {savingBranding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Branding
            </Button>
          </CardContent>
        </Card>

        {/* Invoice Designer */}
        <Card data-testid="invoice-designer-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Type className="w-5 h-5" />
              Invoice Designer
            </CardTitle>
            <CardDescription>Customize your invoice typography, colors, and layout</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Designer Controls */}
              <div className="space-y-6">
                {/* Default Template */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Default Template</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {designerOptions?.templates?.map((key) => {
                      const info = designerOptions?.template_info?.[key] || {};
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleDesignerChange('default_template', key)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            (branding.default_template || 'brutalist') === key 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid={`template-tile-${key}`}
                        >
                          <p className="font-medium text-sm">{info.name || key.charAt(0).toUpperCase() + key.slice(1)}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{info.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Font Pairing */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Typography</Label>
                  {(branding.default_template || 'brutalist') === 'brutalist' && (
                    <p className="text-xs text-muted-foreground" data-testid="brutalist-typography-note">
                      The Brutalist template uses its own signature typefaces — pick another template to apply these pairings.
                    </p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {designerOptions?.font_pairings && Object.entries(designerOptions.font_pairings).map(([key, pairing]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleDesignerChange('font_pairing', key)}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          branding.font_pairing === key 
                            ? 'border-primary bg-primary/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`font-pairing-${key}`}
                      >
                        <p className="font-medium text-sm">{pairing.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{pairing.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color Palette */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Color Palette</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {designerOptions?.palette_presets && Object.entries(designerOptions.palette_presets).map(([key, palette]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleDesignerChange('palette_preset', key)}
                        className={`p-2 rounded-lg border-2 transition-all ${
                          branding.palette_preset === key 
                            ? 'border-primary' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        title={palette.description}
                        data-testid={`palette-${key}`}
                      >
                        <div className="flex gap-1 mb-1">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: palette.primary_color }} />
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: palette.secondary_color }} />
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: palette.accent_color }} />
                        </div>
                        <p className="text-xs font-medium truncate">{palette.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Logo Size */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Logo Size</Label>
                  <div className="flex gap-2">
                    {['small', 'medium', 'large'].map((size) => (
                      <Button
                        key={size}
                        type="button"
                        variant={branding.logo_size === size ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleDesignerChange('logo_size', size)}
                        data-testid={`logo-size-${size}`}
                      >
                        {size.charAt(0).toUpperCase() + size.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Invoice Footer */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Invoice Footer</Label>
                  <Input
                    value={branding.invoice_footer || ''}
                    onChange={(e) => handleDesignerChange('invoice_footer', e.target.value)}
                    placeholder="e.g., Thank you for your business!"
                    maxLength={500}
                    data-testid="invoice-footer-input"
                  />
                  <p className="text-xs text-muted-foreground">Custom text displayed at the bottom of your invoices</p>
                </div>
              </div>

              {/* Live Preview */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Live Preview</Label>
                  {loadingPreview && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Updating...
                    </div>
                  )}
                </div>
                <div className="border rounded-lg bg-muted/30 h-[400px] overflow-auto">
                  {previewPdfUrl ? (
                    <img
                      src={previewPdfUrl}
                      alt="Invoice Preview"
                      className="w-full object-contain"
                      data-testid="designer-preview-image"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <Eye className="w-8 h-8 mb-2" />
                      <p className="text-sm">Make changes to see a live preview</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3"
                        onClick={() => loadDesignerPreview()}
                        data-testid="load-preview-btn"
                      >
                        Load Preview
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={saveBranding} disabled={savingBranding} data-testid="save-designer-btn">
                {savingBranding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Design Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Subscription
            </CardTitle>
            <CardDescription>
              You&apos;re currently on the <span className="font-medium text-foreground">{user?.subscription_plan?.charAt(0).toUpperCase() + user?.subscription_plan?.slice(1) || 'Free'}</span> plan
              {user?.subscription_billing_interval === 'year' ? ' (billed annually)' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center rounded-full border p-1" data-testid="settings-billing-toggle">
                <button
                  type="button"
                  onClick={() => setBillingInterval('month')}
                  data-testid="settings-billing-toggle-monthly"
                  className={`px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-wide transition-colors ${
                    billingInterval === 'month' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingInterval('year')}
                  data-testid="settings-billing-toggle-annual"
                  className={`px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-wide transition-colors ${
                    billingInterval === 'year' ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Annual
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <Card 
                  key={plan.id} 
                  className={`relative ${plan.current ? 'border-primary' : ''} ${plan.popular ? 'shadow-md' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground">Popular</Badge>
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      {plan.name}
                      {plan.current && <Badge variant="secondary">Current</Badge>}
                    </CardTitle>
                    <div className="flex items-baseline gap-1.5 flex-wrap">
                      <span className="text-3xl font-bold">{plan.price}</span>
                      <span className="text-muted-foreground">{plan.period}</span>
                      {plan.savings ? (
                        <Badge variant="outline" className="text-[10px] uppercase" data-testid={`settings-savings-${plan.id}`}>
                          Save {plan.savings}%
                        </Badge>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Expandable extra features */}
                    {plan.extraFeatures && plan.extraFeatures.length > 0 && (
                      <>
                        {expandedPlans[plan.id] && (
                          <ul className="space-y-2 mb-3 pt-2 border-t border-dashed">
                            {plan.extraFeatures.map((feature, index) => (
                              <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Check className="w-4 h-4 text-muted-foreground/60 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleExpandPlan(plan.id)}
                          className="text-xs text-primary hover:underline mb-4 flex items-center gap-1"
                        >
                          {expandedPlans[plan.id] ? (
                            <>
                              <ChevronUp className="w-3 h-3" />
                              Show less
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-3 h-3" />
                              +{plan.extraFeatures.length} more features
                            </>
                          )}
                        </button>
                      </>
                    )}
                    
                    {!plan.current && plan.id !== 'free' && (
                      <Button 
                        className="w-full" 
                        variant={plan.popular ? 'default' : 'outline'}
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={processingPayment}
                        data-testid={`upgrade-to-${plan.id}-btn`}
                      >
                        {processingPayment ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          `Upgrade to ${plan.name}`
                        )}
                      </Button>
                    )}
                    {plan.current && (
                      <div className="space-y-2">
                        <Button className="w-full" variant="secondary" disabled>
                          Current Plan
                        </Button>
                        {/* Switch billing interval for the same tier (only for a live Stripe subscription) */}
                        {plan.id !== 'free' && user?.has_stripe_subscription && billingInterval !== (user?.subscription_billing_interval || 'month') && !user?.subscription_cancel_at_period_end && (
                          <Button
                            className="w-full"
                            variant="outline"
                            onClick={() => handleSwitchInterval()}
                            disabled={switchingInterval}
                            data-testid={`switch-to-${billingInterval}-btn`}
                          >
                            {switchingInterval ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              `Switch to ${billingInterval === 'year' ? 'Annual' : 'Monthly'}`
                            )}
                          </Button>
                        )}
                        {/* Show cancel option for paid plans with an active real Stripe subscription */}
                        {plan.id !== 'free' && user?.has_stripe_subscription && !user?.subscription_cancel_at_period_end && (
                          <Button 
                            variant="outline" 
                            className="w-full text-muted-foreground hover:text-destructive hover:border-destructive"
                            onClick={() => setShowCancelDialog(true)}
                            data-testid="cancel-subscription-btn"
                          >
                            Cancel Subscription
                          </Button>
                        )}
                        {/* Show cancellation pending message */}
                        {plan.id !== 'free' && user?.subscription_cancel_at_period_end && (
                          <p className="text-sm text-center text-amber-600">
                            Cancels on {new Date(user.subscription_current_period_end).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                    {plan.id === 'free' && !plan.current && (
                      <Button className="w-full" variant="outline" disabled>
                        Downgrade
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Promo Code Section */}
            {user?.subscription_plan === 'free' && (
              <div className="mt-8 pt-6 border-t">
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Have a promo code?</h3>
                </div>
                <div className="flex gap-3 items-end">
                  <div className="flex-1 max-w-xs">
                    <Label htmlFor="promo-code" className="sr-only">Promo Code</Label>
                    <Input
                      id="promo-code"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        setPromoValidated(null);
                      }}
                      placeholder="Enter promo code"
                      className="font-mono uppercase"
                      data-testid="promo-code-input"
                    />
                  </div>
                  <Button
                    onClick={applyPromoCode}
                    disabled={applyingPromo || !promoCode.trim()}
                    className="gap-2"
                    data-testid="apply-promo-btn"
                  >
                    {applyingPromo ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Gift className="w-4 h-4" />
                        Apply Code
                      </>
                    )}
                  </Button>
                </div>
                {promoValidated && (
                  <div className="mt-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-2 text-emerald-600">
                      <Check className="w-4 h-4" />
                      <span className="font-medium">{promoValidated.description}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Upgrades you to {promoValidated.plan.charAt(0).toUpperCase() + promoValidated.plan.slice(1)} plan • {promoValidated.remaining_uses} uses remaining
                    </p>
                  </div>
                )}
                <p className="text-sm text-muted-foreground mt-2">
                  Enter a valid promo code to unlock premium features for free
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referral Program */}
        <Card data-testid="referral-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Give a month, get a month
            </CardTitle>
            <CardDescription>
              Share your link with another creative. They get their first month of Standard free — and when they send their first invoice, you get a free month too.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingReferral ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading referral data...
              </div>
            ) : referralData ? (
              <>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={referralData.share_url}
                    readOnly
                    className="flex-1 font-mono text-sm bg-muted/50"
                    data-testid="referral-link-input"
                  />
                  <Button onClick={copyReferralLink} className="gap-2" data-testid="copy-referral-btn">
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </Button>
                </div>
                
                <div className="text-sm text-muted-foreground">
                  Referred so far: <span className="font-medium text-foreground">{referralData.referred_count}</span>
                  {' · '}
                  Credits earned: <span className="font-medium text-foreground">${Number(referralData.credits_earned || 0).toFixed(2)}</span>
                </div>
                
                <p className="text-xs text-muted-foreground pt-2 border-t">
                  Credits apply to your next bill. Earn up to 12 free months per year. Referred friends must verify their email; your month is credited when they send their first invoice.
                  {user?.subscription_billing_interval === 'year' ? ' As an annual subscriber, you earn 1.5x the credit per referral.' : ''}
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Unable to load referral data</p>
            )}
          </CardContent>
        </Card>

        {/* Security — Sign out of all devices */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Security
            </CardTitle>
            <CardDescription>
              Manage active sessions across all your devices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="font-semibold">Sign out of all devices</h3>
                <p className="text-sm text-muted-foreground">
                  Ends every session, including this one. Useful if you&apos;ve signed in somewhere you don&apos;t recognise.
                </p>
              </div>
              <Button
                variant="outline"
                className="gap-2 shrink-0"
                onClick={() => setShowLogoutAllDialog(true)}
                data-testid="logout-all-btn"
              >
                <Lock className="w-4 h-4" />
                Sign out everywhere
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Sign out of all devices confirmation */}
        <Dialog open={showLogoutAllDialog} onOpenChange={setShowLogoutAllDialog}>
          <DialogContent data-testid="logout-all-dialog">
            <DialogHeader>
              <DialogTitle>Sign out of all devices?</DialogTitle>
              <DialogDescription>
                This signs you out everywhere, including this device. You&apos;ll need to log in again.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowLogoutAllDialog(false)}
                disabled={signingOutAll}
                data-testid="logout-all-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLogoutAll}
                disabled={signingOutAll}
                data-testid="logout-all-confirm"
              >
                {signingOutAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing out…
                  </>
                ) : (
                  'Yes, sign out everywhere'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Danger Zone - Delete Account */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions that affect your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-destructive/30 rounded-lg bg-destructive/5">
              <div>
                <h3 className="font-semibold">Delete Account</h3>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>
              <Button 
                variant="destructive" 
                className="gap-2 shrink-0"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="w-4 h-4" />
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delete Account Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Delete Your Account?
              </DialogTitle>
              <DialogDescription>
                This action is permanent and cannot be undone. All your data will be deleted including:
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-destructive" />
                  All invoices and payment history
                </li>
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-destructive" />
                  All clients and contacts
                </li>
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-destructive" />
                  All saved products
                </li>
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-destructive" />
                  Recurring invoices and campaigns
                </li>
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-destructive" />
                  Your branding and settings
                </li>
              </ul>
              
              <div className="space-y-2">
                <Label htmlFor="deleteConfirm">
                  Type <span className="font-mono font-bold text-destructive">DELETE</span> to confirm
                </Label>
                <Input
                  id="deleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="font-mono"
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteAccount}
                disabled={deletingAccount || deleteConfirmText !== 'DELETE'}
                className="gap-2"
              >
                {deletingAccount ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete My Account
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Cancel Subscription</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your subscription?
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm">
                  Your subscription will remain active until the end of the current billing period. 
                  After that, you&apos;ll be downgraded to the free plan.
                </p>
                {user?.subscription_current_period_end && (
                  <p className="text-sm mt-2 font-medium">
                    Your access will end on: {new Date(user.subscription_current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
              {user?.has_stripe_subscription &&
                (user?.subscription_billing_interval || 'month') === 'month' &&
                ANNUAL_PLAN_INFO[user?.subscription_plan] && (
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-3" data-testid="cancel-dialog-annual-offer">
                  <p className="text-sm font-medium">
                    Before you go — save {ANNUAL_PLAN_INFO[user.subscription_plan].savingsPercent}% with annual billing instead
                  </p>
                  <p className="text-sm text-muted-foreground">
                    ${ANNUAL_PLAN_INFO[user.subscription_plan].annualPrice}/year (prorated charge today) — no need to cancel.
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleSwitchInterval('year')}
                    disabled={switchingInterval}
                    data-testid="cancel-dialog-switch-annual-btn"
                  >
                    {switchingInterval ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Switch to Annual Instead'
                    )}
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => setShowCancelDialog(false)}
              >
                Keep Subscription
              </Button>
              <Button 
                variant="destructive"
                onClick={handleCancelSubscription}
                disabled={cancellingSubscription}
                className="gap-2"
                data-testid="confirm-cancel-subscription-btn"
              >
                {cancellingSubscription ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Subscription'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Settings;
